/**
 * PHAN HOI CUA DUONG SUA CHU SAN CO - ket qua chay thu that, hien trong bang
 * Thong tin (khong con nam duoi o go).
 *
 * Nhung dieu quan trong ve mat san pham:
 *   - Ket qua CHAY THU THAT tu loi, khong phai phong doan cua giao dien. Neu loi
 *     tu choi, bang noi ro ly do va nut Áp dụng bi khoa.
 *   - Khi thieu glyph, hien DUNG NHUNG KY TU thieu - thong tin duy nhat co ich
 *     voi nguoi go tieng Viet.
 *   - Khi phai thay font, noi ro HINH THUC TAI LIEU SE DOI, truoc khi bam. Do la
 *     36.8% so lan sua thanh cong - khong phai truong hop hiem. Canh bao nay
 *     PHAI hien trong luc dang sua; no da chuyen vao bang thuoc tinh o popup
 *     nhung van luon thay duoc.
 */
import { SCOPE_LABEL } from '../core/edit/scope';
import { getFontFamily } from '../core/fonts/families';
import { ALIGN_LABEL, readVerdict, type TextReplaceReport } from './textEditCore';
import type { EditSession } from './InlineTextEditor';

/**
 * GHI CHU CAN LE - phan rieng cua duong sua chu san co.
 *
 * Nac dang sang la `align ?? alignDetected` - khi nguoi dung chua dong vao gi,
 * nut sang len chinh la CAN LE MA TA DO DUOC TU CHU CU. Neu khong noi ra, nguoi
 * dung khong phan biet duoc "may nhan ra chu cu can giua" voi "may dang dinh
 * can giua no".
 */
function AlignNote({ session }: { session: EditSession }) {
  const detected = session.report?.alignDetected ?? null;
  const active = session.align ?? detected;
  const auto = session.align === null && detected !== null;

  return (
    <p className="scopeHint">
      {!session.alignSupported ? (
        <>
          Lõi đang chạy chưa nhận tùy chọn căn lề. Chữ mới sẽ giữ đúng căn lề đo được từ chữ
          cũ{detected ? ` (${ALIGN_LABEL[detected].toLowerCase()})` : ''}.
        </>
      ) : auto ? (
        <>
          Căn lề đang theo tài liệu: nhận ra chữ cũ {ALIGN_LABEL[active ?? 'left'].toLowerCase()}.
          Chọn một nút để đổi.
        </>
      ) : (
        <>
          Căn lề áp dụng cho từng dòng theo bề rộng của chính dòng đó.
          {detected && detected !== session.align && (
            <> Chữ cũ vốn {ALIGN_LABEL[detected].toLowerCase()}.</>
          )}
        </>
      )}{' '}
      <span className="scopeKeys">Ctrl+Shift+L / E / R</span>
    </p>
  );
}

/**
 * GHI CHU KIEU CHU - phan rieng cua duong sua chu san co.
 *
 * Bam dam hay nghieng se DOI FONT, vi font nhung trong tai lieu chi co mot mat
 * chu va khong co cach nao lay ban dam cua no ra tu chinh no.
 */
function StyleNote({ session }: { session: EditSession }) {
  const styled = session.bold || session.italic;
  const label = getFontFamily(session.fontFamily).label;
  return (
    <p className="scopeHint">
      {styled ? (
        <>
          Font nhúng trong tài liệu chỉ có một mặt chữ, nên chữ mới sẽ được gõ lại bằng mặt chữ
          thật của {label}. <strong>Hình thức tài liệu sẽ đổi.</strong>
        </>
      ) : (
        <>Để nguyên thì lần sửa này cố giữ đúng font gốc của tài liệu.</>
      )}
    </p>
  );
}

/** Bao cao ve viec dat chu: so dong, co chu cuoi cung, co bi thu nho khong. */
function FitNote({ report }: { report: TextReplaceReport }) {
  const shrunk = report.shrinkRatio !== null && report.shrinkRatio < 0.999;
  return (
    <p className="fitNote">
      {report.lineCount} dòng
      {report.fontSizePt !== null && <> · cỡ {report.fontSizePt.toFixed(1)}pt</>}
      <> · {ALIGN_LABEL[report.align].toLowerCase()}</>
      {shrunk && (
        <>
          {' '}
          · <span className="warnInline">
            đã thu nhỏ còn {Math.round((report.shrinkRatio ?? 1) * 100)}% để vừa khối
          </span>
        </>
      )}
    </p>
  );
}

function codePointLabel(ch: string): string {
  const cp = ch.codePointAt(0) ?? 0;
  return `U+${cp.toString(16).toUpperCase().padStart(4, '0')}`;
}

/**
 * BANG KET QUA CHAY THU + CANH BAO THAY FONT.
 *
 * Day la noi canh bao "hình thức tài liệu sẽ thay đổi" song, va no PHAI thay
 * duoc trong luc sua - popup tu mo khi bat dau sua nen no luon o day.
 */
export function EditFeedback({ session }: { session: EditSession }) {
  const verdict = readVerdict(session.report);

  return (
    <div className="editFeedback">
      <StyleNote session={session} />
      <AlignNote session={session} />

      {session.unsupported ? (
        <div className="editVerdict warn">
          <span className="verdictDot" aria-hidden="true" />
          <div>
            <strong>Không kiểm tra trước được</strong>
            <p>
              Lõi đang chạy không có bước chạy thử. Lần sửa này chỉ được kiểm tra lúc lưu, nên
              có thể bị từ chối muộn.
            </p>
          </div>
        </div>
      ) : session.pending && !session.report ? (
        <div className="editVerdict">
          <span className="spinner small" aria-hidden="true" />
          <div>
            <strong>Đang kiểm tra…</strong>
          </div>
        </div>
      ) : verdict.verdict === 'idle' ? (
        <div className="editVerdict">
          <span className="verdictDot" aria-hidden="true" />
          <div>
            <strong>Hãy gõ nội dung mới</strong>
          </div>
        </div>
      ) : (
        <div
          className={`editVerdict ${
            verdict.verdict === 'blocked'
              ? 'bad'
              : verdict.verdict === 'ok-substituted'
                ? 'warn'
                : 'ok'
          }${session.pending ? ' stale' : ''}`}
        >
          <span className="verdictDot" aria-hidden="true" />
          <div>
            <strong>{verdict.headline}</strong>

            {verdict.missingChars.length > 0 && (
              <p className="missingChars">
                Không có glyph cho:{' '}
                {verdict.missingChars.map((c, i) => (
                  <span className="missingChar" key={`${c}-${i}`} title={codePointLabel(c)}>
                    {c}
                  </span>
                ))}
              </p>
            )}

            <p>{verdict.detail}</p>
            {verdict.fix && <p className="editVerdictFix">{verdict.fix}</p>}

            {/* CANH BAO THAY FONT - yeu cau rieng, khong duoc gop vao ly do chung. */}
            {session.report?.ok && session.report.substituted && (
              <p className="substitutionWarning">
                Hình thức tài liệu <strong>sẽ thay đổi</strong>: chữ mới được gõ lại bằng font
                đóng gói sẵn ({getFontFamily(session.fontFamily).label}), không phải font gốc của
                tài liệu.
                {session.fontCount > 1 && (
                  <>
                    {' '}
                    Phạm vi này ({SCOPE_LABEL[session.scopeKind].toLowerCase()}) dùng{' '}
                    {session.fontCount} font; toàn bộ chữ mới sẽ về một font duy nhất.
                  </>
                )}
              </p>
            )}

            {session.report?.ok && <FitNote report={session.report} />}
          </div>
        </div>
      )}
    </div>
  );
}
